/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { memo } from 'preact/compat';
import { useRef, useCallback, useLayoutEffect, useMemo, StateUpdater } from 'preact/hooks';
import { classNames } from '../utils/UNSAFE_classNames';
import { mergeRefs } from '../utils/PRIVATE_refUtils';
import { warn } from '../utils/UNSAFE_logger';
import { allTabbableElements } from '../utils/PRIVATE_tabbableUtils';
import { useId } from '../hooks/UNSAFE_useId';
import { useAnimation, UseAnimationConfig } from '../hooks/UNSAFE_useAnimation';
import { useResizeObserver } from '../hooks/UNSAFE_useResizeObserver';
import { TabbableModeContext } from '../hooks/UNSAFE_useTabbableMode';
import { FocusTrap } from '../UNSAFE_FocusTrap';
import { SelectionMode, ListItemRendererContext } from '../UNSAFE_Collection';
import { multiVariantStyles, styles } from './themes/CardGridStyles.css';
import { compareListItemContext } from '../utils/PRIVATE_collectionUtils';

export const ITEM_SELECTOR = '[data-oj-key]';

/**
 * Props for the CardGridItem Component
 */
export type Props<K, D> = {
  children: (context: ListItemRendererContext<K, D>) => ComponentChildren;
  context: ListItemRendererContext<K, D>;
  isTabbable: boolean;
  isFocused: boolean;
  isFocusRingVisible: boolean;
  isSelected: boolean;
  selectionMode: SelectionMode;
  focusBehavior: 'card' | 'content';
  setIsFocusBehaviorValid?: StateUpdater<boolean>;
  initialAnimation?: 'slideUp' | 'slideDown';
  isGridLayout?: boolean;
  updateCardSize?: (width: number, height: number) => void;
};

/**
 * The internal component used to render a single item in CardGrid.
 */
export function BaseCardViewItem<K extends string | number, D>({
  children,
  context,
  isTabbable,
  isFocused,
  isFocusRingVisible,
  isSelected,
  selectionMode,
  focusBehavior,
  setIsFocusBehaviorValid,
  initialAnimation,
  isGridLayout,
  updateCardSize
}: Props<K, D>) {
  const rootRef = useRef<HTMLDivElement>(null);
  const itemKey = context.metadata.key;
  const itemIndex = context.index;

  const isItemFocused = isFocused && isFocusRingVisible;
  const variantClasses = multiVariantStyles({
    itemFocused: focusBehavior === 'card' && isItemFocused ? 'isItemFocused' : 'isNotItemFocused',
    itemInitialOpacity: initialAnimation ? 'isTransparent' : 'isNotTransparent',
    itemInGridLayout: isGridLayout ? 'isInGridLayout' : 'isNotInGridLayout'
  });
  const classes = classNames([styles.itemStyle, variantClasses]);

  // don't bubble Arrow Up/Down/Left/Right events to parent when in isTabbable mode
  // this bubbling causes the outer CollectionFocus ring to change index while in the isTabbable mode.
  const handleKeyDown = (event: KeyboardEvent) => {
    if (
      focusBehavior === 'card' &&
      isTabbable &&
      (event.key === 'ArrowDown' ||
        event.key === 'ArrowUp' ||
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowRight')
    ) {
      event.stopPropagation();
    }
  };

  const handleResize = useCallback(
    (entry: ResizeObserverEntry) => {
      updateCardSize?.(entry.contentRect.width, entry.contentRect.height);
    },
    [updateCardSize]
  );

  const nullRef = useRef<HTMLDivElement>(null);
  useResizeObserver({ ref: updateCardSize ? rootRef : nullRef, callback: handleResize });

  useLayoutEffect(() => {
    if (focusBehavior === 'content' && isItemFocused) {
      // rootRef is cardgrid item, its first child is the focus trap div, so its first grand child
      // is the actual content
      const content = rootRef.current?.firstElementChild?.firstElementChild as HTMLElement;
      const focusable = allTabbableElements(content, true, true);
      if (focusable.length === 0 || focusable.length > 1) {
        warn(
          `${focusable.length} focusable elements are detected in the card. 'content' focusBehavior is intended only for use when the card has single focusable element. Please specify one focusable element in the card or switch the focusBehavior to 'card'.`
        );
        setIsFocusBehaviorValid?.(false);
      } else {
        focusable[0].focus();
      }
    }
  }, [focusBehavior, isItemFocused, setIsFocusBehaviorValid]);

  // TODO: issue track in JET-57681, we currently have two useAnimation() because the opacity and
  // slide up/down have different durations, and the current API couldn't handle this case, we will
  // update this once the API is updated

  // animation for slide up/down
  const { nodeRef: nodeRefSlide } = useAnimation<'slideUp' | 'slideDown' | 'none', HTMLDivElement>(
    initialAnimation || 'none',
    animationConfigSlide(itemIndex)
  );
  // animation for opacity change
  const { nodeRef: nodeRefOpacity } = useAnimation<'opacity' | 'none', HTMLDivElement>(
    (initialAnimation && 'opacity') || 'none',
    animationConfigOpacity(itemIndex)
  );

  const itemRefs = useMemo(() => {
    // NOTE: spot an issue when we click the cards during animation is processing, the cards would not finish
    // the current animations and stay as what it is, e.g. leave itself as half transparent.
    // need to remove nodeRefSlide and nodeRefOpacity to avoid this issue.
    if (initialAnimation === undefined) {
      return mergeRefs(rootRef);
    }
    return mergeRefs(rootRef, nodeRefSlide, nodeRefOpacity);
  }, [rootRef, nodeRefSlide, nodeRefOpacity, initialAnimation]);

  return (
    <div
      id={useId()}
      ref={itemRefs}
      class={classes}
      role="gridcell"
      data-oj-key={itemKey}
      onKeyDown={handleKeyDown}
      aria-selected={selectionMode === 'none' ? undefined : isSelected}
      {...(typeof itemKey === 'number' && { 'data-oj-key-type': 'number' })}>
      <TabbableModeContext.Provider value={{ isTabbable }}>
        <FocusTrap isDisabled={!isTabbable} restoreFocusRef={false}>
          {children(context)}
        </FocusTrap>
      </TabbableModeContext.Provider>
    </div>
  );
}

const animationConfigSlide = (
  index: number
): UseAnimationConfig<'slideUp' | 'slideDown' | 'none', HTMLDivElement> => {
  return {
    animationStates: {
      slideUp: {
        from: {
          translateY: '50px'
        },
        to: {
          translateY: '0px'
        },
        options: {
          duration: 300,
          delay: index * 50,
          easing: [0, 0, 0.2, 1]
        }
      },
      slideDown: {
        from: {
          translateY: '-50px'
        },
        to: {
          translateY: '0'
        },
        options: {
          duration: 300,
          delay: index * 50,
          easing: [0, 0, 0.2, 1]
        }
      }
    },
    isAnimatedOnMount: true
  };
};

const animationConfigOpacity = (
  index: number
): UseAnimationConfig<'opacity' | 'none', HTMLDivElement> => {
  return {
    animationStates: {
      opacity: {
        from: {
          opacity: 0
        },
        to: {
          opacity: 1
        },
        options: {
          duration: 150,
          delay: index * 50,
          easing: 'linear'
        }
      }
    },
    isAnimatedOnMount: true
  };
};

export const MemoizeBaseCardViewItem = memo(
  BaseCardViewItem,
  <K, D>(prev: Props<K, D>, next: Props<K, D>) => {
    if (prev && next) {
      return (
        prev.children === next.children &&
        prev.isTabbable === next.isTabbable &&
        prev.isFocused === next.isFocused &&
        prev.isFocusRingVisible === next.isFocusRingVisible &&
        prev.isSelected === next.isSelected &&
        prev.initialAnimation === next.initialAnimation &&
        prev.focusBehavior === next.focusBehavior &&
        compareListItemContext(prev.context, next.context)
      );
    }
    return false;
  }
);
